import * as React from "react";
import type { SVG, StyledComponentBase } from "@/components/component-base.types";
import { Chip as MuiChip } from "@mui/material";
import classNames from "classnames";
import styles from "./chip.module.scss";

export type ChipType = "default" | "success" | "info" | "warning" | "error";

export interface ChipProps extends StyledComponentBase {
  /**
   * chipType determines the coloring of the chip
   */
  chipType?: ChipType;
  /**
   * Instead of the default icon of a circle with an X,
   * a custom icon can be added as the delete Icon
   */
  deleteIcon?: SVG;
  /**
   * A custom icon can be added to the chip
   */
  icon?: SVG;
  /**
   * Label for the Chip. Should be a string
   */
  label: string;
  /**
   * Size options for the chip are small and medium
   */
  size?: "small" | "medium";
  /**
   * Variant options:
   * 
   * "filled": background color with white text and icon
   * 
   * "outlined" transparent background with colored text and icon
   */
  variant?: "filled" | "outlined";
  /**
   * A function called when the chip is dismissed. Does
   * not fire if the chip is not deletable
   */
  onDelete?: () => void;
}

/**
 * Provides a UI element to display small bits of information
 * @param iProps ChipProps props
 * @returns JSX element representing the MUIChip with TB styles/options
 */
export const Chip = (iProps: ChipProps) => {
  const {
    chipType = "default",
    className,
    deleteIcon,
    icon,
    label,
    size = "small",
    variant = "filled",
    onDelete,
  } = iProps;

  let chipIcon;
  if (icon) {
    const Svg = icon as SVG;
    chipIcon = <Svg className={styles.icon} />;
  }

  let delIcon;
  if (deleteIcon) {
    const Svg = deleteIcon as SVG;
    delIcon = <Svg className={styles.icon} />;
  }

  return (
    <MuiChip
      className={classNames(styles.chip, styles[chipType], className)}
      color={chipType}
      deleteIcon={delIcon}
      icon={chipIcon}
      label={label}
      size={size}
      variant={variant}
      onDelete={onDelete}
    />
  );
};
